import { Email, HelpOutlineOutlined } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormHelperText,
	Grid,
	Icon,
	IconButton,
	InputAdornment,
	MenuItem,
	Modal,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import * as yup from "yup";
import { HtmlTooltip } from "../../../../infra/components/tooltipsCustom/tooltipsCuston";
import { ToastContent } from "../../../../shared/components";
import { AutoCompleteClientSelect } from "../../../../shared/components/AutoCompleteClientSelect/AutoCompleteClientSelect";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { IVFormErros, useVForm, VForm } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { VTextField } from "../../../../shared/forms/VTextField";
import { useToast } from "../../../../shared/hooks";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import { SetupChargeApiError, SetupChargeService } from "../../../../shared/services/api/setupCharge/SetupChargeService";
import { ISetupChargeConfig, ISetupChargeErrorDetail, ISetupChargeRequest } from "../../../../shared/services/api/setupCharge/Types";
import { extractApiErrorMessage, globalColorTytle, globalMaskDocument, globalMaskReal, globalRemoveMaskReal2, RangeStates } from "../../../../shared/utils";
import { searchZipcode } from "../../utils/setupChargeAddress";
import { isValidDocument, isValidPhone, onlyDigits } from "../../utils/setupChargeValidators";

interface ModalCreateSetupChargeProps {
	isOpen: boolean;
	onClose: () => void;
	/** Chamado quando a cobrança foi criada (ou pode ter sido), para a tabela recarregar. */
	onCreated: () => void;
}

type TPhase = "form" | "sending" | "waiting" | "done";

type FormData = {
	payerName: string;
	payerDocument: string;
	payerEmail: string;
	payerPhone: string;
	addressZipcode: string;
	addressStreet: string;
	addressNumber: string;
	addressComplement: string;
	addressDistrict: string;
	addressCity: string;
	addressState: string;
	amount: string;
};

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 30;

/** Caminho do campo na API (contrato do Back 13) para o nome do campo no formulário. */
const API_FIELD_TO_FORM_FIELD: Record<string, keyof FormData> = {
	"payer.name": "payerName",
	"payer.document": "payerDocument",
	"payer.email": "payerEmail",
	"payer.phone": "payerPhone",
	"property.zipcode": "addressZipcode",
	"property.street": "addressStreet",
	"property.number": "addressNumber",
	"property.complement": "addressComplement",
	"property.district": "addressDistrict",
	"property.city": "addressCity",
	"property.state": "addressState",
};

const validationSchema = yup.object().shape({
	payerName: yup.string().trim().required("Informe o nome do cliente"),
	payerDocument: yup
		.string()
		.required("Informe o CPF ou CNPJ")
		.test("valid-document", "CPF ou CNPJ inválido", (value) => isValidDocument(value)),
	payerEmail: yup.string().trim().required("Informe o e-mail").email("E-mail inválido"),
	payerPhone: yup
		.string()
		.required("Informe o celular")
		.test("valid-phone", "Celular inválido", (value) => isValidPhone(value)),
	addressZipcode: yup
		.string()
		.required("Informe o CEP")
		.test("valid-zipcode", "CEP inválido", (value) => onlyDigits(value).length === 8),
	addressStreet: yup.string().trim().required("Informe a rua"),
	addressNumber: yup.string().trim().required("Informe o número"),
	addressComplement: yup.string().notRequired(),
	addressDistrict: yup.string().trim().required("Informe o bairro"),
	addressCity: yup.string().trim().required("Informe a cidade"),
	addressState: yup.string().trim().required("Selecione o estado"),
	amount: yup.string().notRequired(),
});

const formatCurrency = (value: number): string => `R$ ${globalMaskReal(value.toFixed(2))}`;

export const ModalCreateSetupCharge: React.FC<ModalCreateSetupChargeProps> = ({ isOpen, onClose, onCreated }) => {
	const { formRef } = useVForm();
	const toastHandler = useToast();

	const [phase, setPhase] = useState<TPhase>("form");
	const [formKey, setFormKey] = useState(0);
	const [clientId, setClientId] = useState<string | null>(null);
	const [clientError, setClientError] = useState<string | null>(null);
	const [config, setConfig] = useState<ISetupChargeConfig | null>(null);
	const [isLoadingConfig, setIsLoadingConfig] = useState(false);
	const [configError, setConfigError] = useState<string | null>(null);
	const [selectedAmount, setSelectedAmount] = useState("");
	const [amountError, setAmountError] = useState<string | null>(null);
	const [showAmountAlert, setShowAmountAlert] = useState(false);
	const [bannerError, setBannerError] = useState<string | null>(null);
	const [result, setResult] = useState<ISetupChargeRequest | null>(null);
	const [timedOut, setTimedOut] = useState(false);
	const [isLookingUpDocument, setIsLookingUpDocument] = useState(false);
	const [isLookingUpZipcode, setIsLookingUpZipcode] = useState(false);

	const isActiveRef = useRef(false);
	const timerRef = useRef<number | undefined>(undefined);
	const lastDocumentRef = useRef("");
	const lastZipcodeRef = useRef("");
	const autoFilledNameRef = useRef("");
	const numberInputRef = useRef<HTMLInputElement | null>(null);
	const zipcodeInputRef = useRef<HTMLInputElement | null>(null);

	const rule = config?.valueRule;
	const isEnabled = config?.enabled !== false;
	const isBusy = phase === "sending" || isLoadingConfig;

	const modalStyle = useMemo(
		() => ({
			position: "relative" as const,
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: "min(1040px, 96vw)",
			maxHeight: "94vh",
			overflowY: "auto" as const,
			bgcolor: "background.paper",
			border: "1px solid #605e5e",
			boxShadow: 24,
			p: 2,
			borderRadius: "6px",
		}),
		[],
	);

	const showToastError = (title: string, content: string) => {
		toastHandler.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title={title} content={content} />,
		});
	};

	/** Mantém o valor dentro dos limites da imobiliária (mesmo comportamento do formulário da análise). */
	const clampToRule = (value: number): number => {
		if (rule?.type !== "range") return value;
		const min = rule.min ?? 0;
		const max = rule.max ?? Number.POSITIVE_INFINITY;
		return Math.min(Math.max(value, min), max);
	};

	const limitsText = (): string => {
		if (rule?.type !== "range") return "";
		const { min, max } = rule;
		if (min !== undefined && max !== undefined) return `O valor da taxa de setup deverá estar entre ${formatCurrency(min)} e ${formatCurrency(max)}`;
		if (min !== undefined) return `O valor da taxa de setup deverá ser de no mínimo ${formatCurrency(min)}`;
		if (max !== undefined) return `O valor da taxa de setup deverá ser de no máximo ${formatCurrency(max)}`;
		return "";
	};

	const stopPolling = useCallback(() => {
		if (timerRef.current !== undefined) window.clearTimeout(timerRef.current);
		timerRef.current = undefined;
	}, []);

	const resetAll = useCallback(() => {
		stopPolling();
		lastDocumentRef.current = "";
		lastZipcodeRef.current = "";
		autoFilledNameRef.current = "";
		setPhase("form");
		setClientId(null);
		setClientError(null);
		setConfig(null);
		setConfigError(null);
		setSelectedAmount("");
		setAmountError(null);
		setShowAmountAlert(false);
		setBannerError(null);
		setResult(null);
		setTimedOut(false);
		setIsLookingUpDocument(false);
		setIsLookingUpZipcode(false);
		setFormKey((current) => current + 1);
	}, [stopPolling]);

	useEffect(() => {
		if (!isOpen) return;
		isActiveRef.current = true;
		resetAll();
		return () => {
			isActiveRef.current = false;
			stopPolling();
		};
	}, [isOpen, resetAll, stopPolling]);

	// A regra de valores e a habilitação vêm da configuração da imobiliária escolhida.
	useEffect(() => {
		setConfig(null);
		setConfigError(null);
		setSelectedAmount("");
		setAmountError(null);
		setShowAmountAlert(false);
		if (!clientId) return;

		let isCurrent = true;
		setIsLoadingConfig(true);
		SetupChargeService.getConfig(clientId).then((response) => {
			if (!isCurrent) return;
			setIsLoadingConfig(false);
			if (response instanceof Error) {
				setConfigError(response.message);
				return;
			}
			setConfig(response);
		});

		return () => {
			isCurrent = false;
		};
	}, [clientId]);

	// Com faixa de valores, o campo já nasce no mínimo permitido.
	useEffect(() => {
		const valueRule = config?.valueRule;
		if (!valueRule || valueRule.type !== "range" || valueRule.min === undefined) return;
		const initial = globalMaskReal(clampToRule(valueRule.min).toFixed(2));
		const timeoutId = window.setTimeout(() => formRef.current?.setFieldValue("amount", initial), 0);
		return () => window.clearTimeout(timeoutId);
	}, [config]);

	const lookupDocument = async (digits: string, maskedValue: string) => {
		lastDocumentRef.current = digits;
		setIsLookingUpDocument(true);

		try {
			const data = await PeopleService.getPersonDataByDocument(digits);
			if (!isActiveRef.current || lastDocumentRef.current !== digits) return;

			const person = data?.naturalPersonData;
			if (!person?.name) {
				showToastError("Documento não localizado", `Documento ${globalMaskDocument(maskedValue)} não localizado. Preencha os campos manualmente.`);
				return;
			}

			const currentName = String(formRef.current?.getFieldValue("payerName") ?? "").trim();
			if (!currentName || currentName === autoFilledNameRef.current) {
				formRef.current?.setFieldValue("payerName", person.name);
				autoFilledNameRef.current = person.name;
			}

			const currentEmail = String(formRef.current?.getFieldValue("payerEmail") ?? "").trim();
			if (person.email && !currentEmail) formRef.current?.setFieldValue("payerEmail", person.email);

			const currentPhone = onlyDigits(formRef.current?.getFieldValue("payerPhone"));
			const knownPhone = onlyDigits(person.mobilePhone || person.phone);
			if (knownPhone && !currentPhone) formRef.current?.setFieldValue("payerPhone", knownPhone);
		} catch (error) {
			if (!isActiveRef.current || lastDocumentRef.current !== digits) return;
			// a consulta é só uma ajuda: o erro não bloqueia o preenchimento manual
			showToastError("Erro na pesquisa", extractApiErrorMessage(error, `Erro na pesquisa do documento ${globalMaskDocument(maskedValue)}`).message);
		} finally {
			if (isActiveRef.current && lastDocumentRef.current === digits) setIsLookingUpDocument(false);
		}
	};

	/** Como na análise, a busca roda ao sair do campo; documento inválido é apontado na hora. */
	const handleDocumentBlur = (maskedValue: string) => {
		const digits = onlyDigits(maskedValue);
		if (digits.length !== 11 && digits.length !== 14) return;

		if (!isValidDocument(digits)) {
			formRef.current?.setErrors({ payerDocument: "CPF ou CNPJ inválido" });
			return;
		}
		if (lastDocumentRef.current === digits) return;

		lookupDocument(digits, maskedValue);
	};

	const handleZipcodeChange = async (rawValue: string) => {
		const digits = onlyDigits(rawValue);
		if (digits.length !== 8) {
			lastZipcodeRef.current = "";
			return;
		}
		if (lastZipcodeRef.current === digits) return;

		lastZipcodeRef.current = digits;
		setIsLookingUpZipcode(true);
		try {
			const address = await searchZipcode(digits);
			if (!isActiveRef.current || lastZipcodeRef.current !== digits) return;

			if (!address) {
				showToastError("Erro de CEP", "CEP não localizado. Verifique o valor digitado.");
				["addressStreet", "addressDistrict", "addressCity", "addressState"].forEach((field) => formRef.current?.setFieldValue(field, ""));
				window.setTimeout(() => zipcodeInputRef.current?.focus(), 0);
				return;
			}

			formRef.current?.setFieldValue("addressStreet", address.street);
			formRef.current?.setFieldValue("addressDistrict", address.district);
			formRef.current?.setFieldValue("addressCity", address.city);
			formRef.current?.setFieldValue("addressState", address.state);
			window.setTimeout(() => numberInputRef.current?.focus(), 0);
		} catch (error) {
			if (!isActiveRef.current || lastZipcodeRef.current !== digits) return;
			showToastError("Erro na consulta", extractApiErrorMessage(error, "Ocorreu um problema ao buscar o CEP. Tente novamente.").message);
			window.setTimeout(() => zipcodeInputRef.current?.focus(), 0);
		} finally {
			if (isActiveRef.current && lastZipcodeRef.current === digits) setIsLookingUpZipcode(false);
		}
	};

	const applyDetails = (message: string, details: ISetupChargeErrorDetail[] = []) => {
		const fieldErrors: IVFormErros = {};
		details.forEach((detail) => {
			const field = detail.field ?? "";
			const text = detail.message || message;
			if (field === "clientId") setClientError(text);
			else if (field === "amount") setAmountError(text);
			else if (API_FIELD_TO_FORM_FIELD[field]) fieldErrors[API_FIELD_TO_FORM_FIELD[field]] = text;
		});
		formRef.current?.setErrors(fieldErrors);
		setBannerError(message);
	};

	const handleTrackFinished = (request: ISetupChargeRequest) => {
		if (request.state === "completed") {
			setResult(request);
			setPhase("done");
			onCreated();
			return;
		}

		// recusada ou parada: volta ao formulário com o motivo exato e os campos marcados
		const message = request.resultMessage || "A solicitação não foi concluída.";
		setPhase("form");
		applyDetails(message, request.resultDetails);
	};

	const trackRequest = (requestId: string, attempt = 0) => {
		timerRef.current = window.setTimeout(
			async () => {
				if (!isActiveRef.current) return;
				const request = await SetupChargeService.getRequest(requestId);
				if (!isActiveRef.current) return;

				if (request instanceof Error) {
					// a solicitação já foi enviada: não volta ao formulário (evita criar de novo), só avisa
					setBannerError(request.message);
					setPhase("done");
					onCreated();
					return;
				}

				if (request.state === "completed" || request.state === "rejected" || request.state === "dead") {
					handleTrackFinished(request);
					return;
				}

				if (attempt + 1 >= POLL_MAX_ATTEMPTS) {
					setTimedOut(true);
					setPhase("done");
					onCreated();
					return;
				}

				trackRequest(requestId, attempt + 1);
			},
			attempt === 0 ? 0 : POLL_INTERVAL_MS,
		);
	};

	const validateAmount = (formAmount: string): number | null => {
		if (rule?.type === "list") {
			const value = Number(selectedAmount);
			if (!selectedAmount || !Number.isFinite(value) || value <= 0) {
				setAmountError("Selecione o valor da taxa");
				return null;
			}
			if (rule.values && !rule.values.includes(value)) {
				setAmountError("Valor não permitido para esta imobiliária");
				return null;
			}
			return value;
		}

		const numeric = Number(globalRemoveMaskReal2(String(formAmount ?? "")));
		if (!Number.isFinite(numeric) || numeric <= 0) {
			setAmountError("Informe o valor da taxa");
			return null;
		}

		// valor fora dos limites é ajustado ao limite, nunca enviado
		const value = clampToRule(numeric);
		if (value !== numeric) {
			formRef.current?.setFieldValue("amount", globalMaskReal(value.toFixed(2)));
			setShowAmountAlert(true);
		}
		return value;
	};

	const handleAmountBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		if (rule?.type !== "range") return;
		const numeric = Number(globalRemoveMaskReal2(String(event.target.value ?? "")));
		if (!Number.isFinite(numeric) || numeric <= 0) return;

		const clamped = clampToRule(numeric);
		if (clamped !== numeric) {
			formRef.current?.setFieldValue("amount", globalMaskReal(clamped.toFixed(2)));
			setShowAmountAlert(true);
		} else {
			setShowAmountAlert(false);
		}
	};

	const handleSubmit = async (data: FormData) => {
		setBannerError(null);
		setClientError(null);
		setAmountError(null);
		formRef.current?.setErrors({});

		// valida tudo de uma vez: todos os campos com problema aparecem juntos
		const errors: IVFormErros = {};
		try {
			await validationSchema.validate(data, { abortEarly: false });
		} catch (err) {
			const validationError = err as yup.ValidationError;
			validationError.inner?.forEach((error) => {
				if (error.path && !errors[error.path]) errors[error.path] = error.message;
			});
		}
		formRef.current?.setErrors(errors);

		let hasError = Object.keys(errors).length > 0;
		if (!clientId) {
			setClientError("Selecione a imobiliária");
			hasError = true;
		}
		const amount = validateAmount(data.amount);
		if (amount === null) hasError = true;
		if (hasError || !clientId || amount === null) return;

		setPhase("sending");
		const created = await SetupChargeService.create({
			clientId,
			externalId: uuid(),
			payer: {
				name: data.payerName.trim(),
				document: onlyDigits(data.payerDocument),
				email: data.payerEmail.trim(),
				phone: onlyDigits(data.payerPhone),
			},
			property: {
				zipcode: onlyDigits(data.addressZipcode),
				street: data.addressStreet.trim(),
				number: data.addressNumber.trim(),
				...(data.addressComplement?.trim() ? { complement: data.addressComplement.trim() } : {}),
				district: data.addressDistrict.trim(),
				city: data.addressCity.trim(),
				state: data.addressState.trim().toUpperCase(),
			},
			amount,
		});

		if (!isActiveRef.current) return;

		if (created instanceof Error) {
			setPhase("form");
			applyDetails(created.message, created instanceof SetupChargeApiError ? created.details : []);
			return;
		}

		setPhase("waiting");
		trackRequest(created.requestId);
	};

	const handleClose = () => {
		if (phase === "sending") return;
		if (phase === "waiting") onCreated();
		onClose();
	};

	const handleCopyLink = async (link: string) => {
		try {
			await navigator.clipboard.writeText(link);
			toastHandler.present({
				type: "success",
				position: "top-right",
				durationMs: 3000,
				messageOrContent: <ToastContent title="Link copiado" content="O link de pagamento foi copiado." />,
			});
		} catch {
			showToastError("Não foi possível copiar", "Selecione o link e copie manualmente.");
		}
	};

	const renderAmountField = () => {
		if (rule?.type === "list") {
			return (
				<Grid item xs={12} md={4}>
					<TextField
						select
						fullWidth
						label="Valor Taxa de Setup"
						value={selectedAmount}
						disabled={isBusy}
						error={!!amountError}
						onChange={(event) => {
							setSelectedAmount(event.target.value);
							setAmountError(null);
						}}
					>
						{(rule.values ?? []).map((value) => (
							<MenuItem key={value} value={String(value)}>
								{formatCurrency(value)}
							</MenuItem>
						))}
					</TextField>
					{amountError && <FormHelperText error>{amountError}</FormHelperText>}
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12} md={3}>
					<VMaskTextField
						fullWidth
						label="Valor Taxa de Setup"
						name="amount"
						mask="real"
						disabled={isBusy || !clientId}
						onBlur={handleAmountBlur}
						onChange={() => {
							setAmountError(null);
							setShowAmountAlert(false);
						}}
					/>
					{amountError && <FormHelperText error>{amountError}</FormHelperText>}
				</Grid>
				{limitsText() && (
					<Grid item>
						<HtmlTooltip title={limitsText()}>
							<IconButton color="primary">
								<HelpOutlineOutlined />
							</IconButton>
						</HtmlTooltip>
					</Grid>
				)}
				{showAmountAlert && limitsText() && (
					<Grid item xs={12} md={5}>
						<Alert severity="warning" sx={{ borderRadius: 3 }}>
							{limitsText()}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	const renderForm = () => (
		<VForm key={formKey} ref={formRef} onSubmit={handleSubmit} placeholder="" onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}}>
			<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
				<Grid container direction="column" padding={2} spacing={2}>
					<Grid item xs={11}>
						<Typography variant="h6" color={globalColorTytle}>
							Dados do Inquilino
						</Typography>
					</Grid>

					<Grid container item direction="row" spacing={2} marginBottom={2}>
						<Grid item xs={12} sm={12} md={4}>
							<AutoCompleteClientSelect
								value={clientId}
								onChange={(id) => {
									setClientId(id);
									setClientError(null);
								}}
								labelSelect="Imobiliária"
								disabled={isBusy}
							/>
							{clientError && <FormHelperText error>{clientError}</FormHelperText>}
						</Grid>
						<Grid item xs={12} sm={12} md={8}>
							{configError ? (
								<Alert severity="error" sx={{ borderRadius: 3 }}>
									{configError}
								</Alert>
							) : config && !isEnabled ? (
								<Alert severity="warning" sx={{ borderRadius: 3 }}>
									A taxa de setup avulsa não está habilitada para esta imobiliária.
								</Alert>
							) : (
								<Alert severity="info" sx={{ borderRadius: 3 }}>
									Atenção: É obrigatório o preenchimento do e-mail e do celular do cliente, pois o link de pagamento é enviado por eles.
								</Alert>
							)}
						</Grid>

						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VMaskTextField
									fullWidth
									label="CPF / CNPJ"
									name="payerDocument"
									mask="cpfOrCnpj"
									disabled={isBusy}
									onBlur={(event) => handleDocumentBlur(event.target.value)}
								/>
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField fullWidth label="Nome" name="payerName" disabled={isBusy} />
							</Grid>
							<Grid item xs={12} sm={12} md={4}>
								<VTextField
									fullWidth
									label="Email"
									name="payerEmail"
									disabled={isBusy}
									trimEmptySpaces
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">
												<IconButton aria-label="email" edge="end" tabIndex={-1}>
													<Email />
												</IconButton>
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						</Grid>

						<Grid container item direction="row" spacing={2}>
							<Grid item xs={12} sm={12} md={4}>
								<VMaskTextField fullWidth label="Celular" name="payerPhone" mask="cell" disabled={isBusy} />
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
				<Grid container direction="column" padding={2} spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6" color={globalColorTytle}>
							Dados do Imóvel
						</Typography>
					</Grid>

					<Grid container item spacing={2}>
						<Grid item xs={12} md={4}>
							<VMaskTextField
								fullWidth
								label="CEP"
								name="addressZipcode"
								mask="cep"
								disabled={isBusy}
								inputRef={zipcodeInputRef}
								onChange={(event) => handleZipcodeChange(event.target.value)}
							/>
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Rua" name="addressStreet" disabled={isBusy} />
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Número" name="addressNumber" inputRef={numberInputRef} disabled={isBusy} />
						</Grid>
					</Grid>

					<Grid container item spacing={2}>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Complemento" name="addressComplement" disabled={isBusy} />
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Bairro" name="addressDistrict" disabled={isBusy} />
						</Grid>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Cidade" name="addressCity" disabled={isBusy} />
						</Grid>
					</Grid>

					<Grid container item spacing={2}>
						<Grid item xs={12} md={4}>
							<VTextField fullWidth label="Estado" name="addressState" disabled={isBusy} select>
								{RangeStates.map(({ value, label }) => (
									<MenuItem key={value} value={value}>
										{label}
									</MenuItem>
								))}
							</VTextField>
						</Grid>
					</Grid>
				</Grid>
			</Box>

			<Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
				<Grid container direction="column" padding={2} spacing={2}>
					<Grid item xs={12}>
						<Typography variant="h6" color={globalColorTytle}>
							Taxa de Setup
						</Typography>
					</Grid>
					<Grid container item spacing={2} alignItems="center">
						{renderAmountField()}
					</Grid>
				</Grid>
			</Box>

			<Box mt={2} display="flex" gap={2} justifyContent="center">
				<Button color="primary" disableElevation variant="outlined" startIcon={<Icon>arrow_back</Icon>} onClick={handleClose} disabled={phase === "sending"}>
					Voltar
				</Button>
				<Button
					color="primary"
					disableElevation
					variant="contained"
					startIcon={<Icon>check</Icon>}
					type="submit"
					disabled={isBusy || (!!config && !isEnabled) || !!configError}
				>
					Gerar taxa
				</Button>
			</Box>
		</VForm>
	);

	const renderWaiting = () => (
		<Box display="flex" flexDirection="column" alignItems="center" gap={2} py={5}>
			<CircularProgress />
			<Typography variant="h6">Gerando cobrança...</Typography>
			<Typography variant="body2" color="text.secondary" align="center">
				Você pode fechar esta janela: a cobrança aparece na tabela assim que for gerada.
			</Typography>
			<Button variant="outlined" onClick={handleClose}>
				Fechar
			</Button>
		</Box>
	);

	const renderDone = () => (
		<Box display="flex" flexDirection="column" gap={2} py={2}>
			{result?.state === "completed" && (
				<Alert severity="success" sx={{ borderRadius: 3 }}>
					Taxa de setup gerada com sucesso. O link de pagamento foi enviado para o e-mail do cliente.
				</Alert>
			)}
			{timedOut && (
				<Alert severity="info" sx={{ borderRadius: 3 }}>
					A cobrança ainda está sendo gerada e aparecerá na tabela em instantes.
				</Alert>
			)}
			{bannerError && (
				<Alert severity="error" sx={{ borderRadius: 3 }}>
					{bannerError}
				</Alert>
			)}
			{result?.paymentLink && (
				<TextField
					fullWidth
					label="Link de pagamento"
					value={result.paymentLink}
					InputProps={{
						readOnly: true,
						endAdornment: (
							<InputAdornment position="end">
								<Tooltip title="Copiar link">
									<IconButton aria-label="Copiar link" edge="end" onClick={() => handleCopyLink(result.paymentLink ?? "")}>
										<Icon>content_copy</Icon>
									</IconButton>
								</Tooltip>
							</InputAdornment>
						),
					}}
				/>
			)}
			<Box display="flex" gap={2} justifyContent="center">
				<Button variant="outlined" onClick={resetAll}>
					Nova taxa
				</Button>
				<Button variant="contained" disableElevation onClick={onClose}>
					Fechar
				</Button>
			</Box>
		</Box>
	);

	return (
		<>
			<LoadingDialog isLoading={isLookingUpDocument} loadingText="Localizando documento..." />
			<LoadingDialog isLoading={isLookingUpZipcode} loadingText="Localizando CEP..." />
			<Modal
				open={isOpen}
				aria-labelledby="modal-create-setup-charge-title"
				onClose={(_, reason) => {
					if (reason === "backdropClick") return;
					handleClose();
				}}
			>
				<Box sx={modalStyle}>
					<LoadingComp isopen={phase === "sending"} />
					<Box position="relative" mb={0.5}>
						<Typography id="modal-create-setup-charge-title" variant="h5" color="primary" sx={{ fontWeight: 400, textAlign: "center", pr: 4 }}>
							Criar nova Taxa de Setup
						</Typography>
						<Typography variant="body2" color="text.secondary" align="center">
							Preencha os dados do cliente e do imóvel para gerar a cobrança de setup avulsa.
						</Typography>
						<IconButton aria-label="Fechar" onClick={handleClose} size="small" disabled={phase === "sending"} sx={{ position: "absolute", right: 0, top: 0 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>

					<Box mt={1}>
						{bannerError && phase === "form" && (
							<Box mx={1} mb={1}>
								<Alert severity="error" sx={{ borderRadius: 3 }}>
									{bannerError}
								</Alert>
							</Box>
						)}
						{(phase === "form" || phase === "sending") && renderForm()}
						{phase === "waiting" && renderWaiting()}
						{phase === "done" && renderDone()}
					</Box>
				</Box>
			</Modal>
		</>
	);
};
